/**
 * Axios Client Configuration
 *
 * Configures axios instance with interceptors for:
 * - Automatic token injection
 * - Token refresh on expiration
 * - Error handling
 */

import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "../store/useAuthStore";
import { AuthErrorCode } from "../types/auth";
import { toast } from "./use-toast";

// Create axios instance
export const axiosClient = axios.create({
  baseURL: "/",
  headers: {
    "Content-Type": "application/json",
  },
  // withCredentials: false by default - no need for cookies with external API
  // Authentication is handled via Bearer token in Authorization header
});

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: Error | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

// Request interceptor - Add authorization and CSRF headers
axiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();

    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Add CSRF token from cookie
    const csrfToken = document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1];
    if (csrfToken && config.headers) {
      config.headers['X-XSRF-TOKEN'] = decodeURIComponent(csrfToken);
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh
axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Parse error response
    const errorData = error.response?.data as {
      errors?: Array<{ code: string; message: string }>;
    };

    const errorCode = errorData?.errors?.[0]?.code;

    // Handle JWT Access Token Expired (101104)
    if (
      errorCode === AuthErrorCode.JWT_ACCESS_TOKEN_EXPIRED &&
      originalRequest &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return axiosClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { accessToken, refreshToken } = useAuthStore.getState();

        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // Call token refresh endpoint
        const response = await axios.post("/v1/auth/token/refresh", {
          accessToken,
          refreshToken,
        });

        const { data, errors } = response.data;

        if (data?.accessToken) {
          // Update access token in store
          useAuthStore.getState().setAccessToken(data.accessToken);

          // Update authorization header for the retry
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
          }

          processQueue(null);
          return axiosClient(originalRequest);
        } else {
          throw new Error(errors?.[0]?.message || "Token refresh failed");
        }
      } catch (refreshError) {
        processQueue(refreshError as Error);

        // Refresh failed, logout user
        useAuthStore.getState().logout();
        window.location.href = "/";

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other auth errors that should trigger logout
    if (
      errorCode &&
      [
        AuthErrorCode.JWT_TOKEN_INVALID,
        AuthErrorCode.JTI_TOKEN_REVOKED,
        AuthErrorCode.JWT_REFRESH_TOKEN_EXPIRED,
        AuthErrorCode.USER_SELF_LOCKED,
        AuthErrorCode.USER_ARCHIVED,
        AuthErrorCode.USER_LOCKED,
      ].includes(errorCode as AuthErrorCode)
    ) {
      useAuthStore.getState().logout();
      window.location.href = "/";

      toast({
        variant: "destructive",
        title: "Session Expired",
        description: "Your session has expired. Please login again.",
      });

      return Promise.reject(error);
    }

    // Don't show toast here - let the calling component handle error display
    // This avoids duplicate toasts when components have their own error handling
    return Promise.reject(error);
  }
);

export default axiosClient;
